import { ToggleState } from './ToggleState';
import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';
import { QueryStatus, queryStatus } from '../model';
import { EventEmitter } from 'events';
import { TypedEmitter } from '../util';

type InteractionFn = () => void | Promise<unknown>;

export interface ModalStateParams {
  onConfirm?: InteractionFn;
  onClose?: InteractionFn;
}

interface CloseOptions {
  result: 'confirmed' | 'declined' | 'dismissed';
}

export class ModalState {
  public readonly openToggle = new ToggleState();
  public readonly toggle = this.openToggle.toggle;

  readonly onConfirm?: InteractionFn;
  readonly onClose?: InteractionFn;
  confirming: QueryStatus = queryStatus.idle;

  readonly events = new EventEmitter() as TypedEmitter<{
    open: () => void;
    close: (options: CloseOptions) => void;
    requestClose: () => void;
  }>;

  constructor(params: ModalStateParams = {}) {
    makeObservable(this, {
      confirming: observable,
      open: action.bound,
      confirm: action.bound,
      close: action.bound,
      isOpen: computed,
    });

    this.onConfirm = params.onConfirm;
    this.onClose = params.onClose;
  }

  open() {
    this.events.emit('open');
    this.openToggle.setOn();
  }

  promise() {
    return new Promise<CloseOptions>((resolve) => {
      this.events.once('close', (options) => {
        resolve(options);
      });
    });
  }

  async confirm() {
    try {
      this.confirming = queryStatus.loading;
      await this.onConfirm?.();
      runInAction(() => {
        this.confirming = queryStatus.success;
        this.events.emit('close', {
          result: 'confirmed',
        });
        return this.openToggle.setOff();
      });
    } catch (e) {
      runInAction(() => {
        this.confirming = queryStatus.error;
      });
    }
  }

  close() {
    this.onClose?.();
    this.events.emit('close', { result: 'dismissed' });
    return this.openToggle.setOff();
  }

  decline() {
    this.onClose?.();
    this.events.emit('close', { result: 'declined' });
    return this.openToggle.setOff();
  }

  requestClose = () => {
    this.events.emit('requestClose');
  };

  get isOpen() {
    return this.openToggle.isOn;
  }
}
